import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useEmployees } from './useSettings';
import { calibrationCommitteeAPI } from '../services/strategicPerformanceApi';

export function useCalibrationAccess() {
  const { user } = useAuth();
  const { data: employees = [] } = useEmployees();
  const [isCommitteeMember, setIsCommitteeMember] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Find current user's employee record
  const currentEmployee = employees.find(emp => emp.userId === user?.id);

  const checkAccess = useCallback(async () => {
    // Admin always has access, no need to check API
    const isAdmin = user?.isStrategicPerformanceSuperUser || false;
    if (isAdmin) {
      setIsCommitteeMember(false); // Not a member, but has admin access
      setIsLoading(false);
      return;
    }

    if (!user?.id || !currentEmployee?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      // Check if user is a calibration committee member for any scorecard
      const members = await calibrationCommitteeAPI.getAll();
      // Check if current user's employee ID matches any member's employeeId
      const isMember = members.some(m => {
        const memberEmployeeId = typeof m.employeeId === 'object' ? m.employeeId?.id : m.employeeId;
        return memberEmployeeId === currentEmployee.id;
      });
      setIsCommitteeMember(isMember);
    } catch (error: any) {
      // If API doesn't exist (404) or other error, silently fail
      // Only log if it's not a 404 (backend not implemented yet)
      if (error?.response?.status !== 404) {
        console.error('Error checking calibration committee membership:', error);
      }
      setIsCommitteeMember(false);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, currentEmployee?.id, user?.isStrategicPerformanceSuperUser]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  // Admin or committee member can access calibration
  const isAdmin = user?.isStrategicPerformanceSuperUser || false;
  const hasCalibrationAccess = isAdmin || isCommitteeMember;

  return { hasCalibrationAccess, isLoading };
}
